import React, { useRef, useState, useCallback, memo } from 'react';
import {
  Button,
  Box,
  Icon,
  Center,
  Image,
  SimpleGrid,
  Input,
  Text,
  IconButton,
  Spinner,
  useBreakpointValue,
  Flex,
  ButtonGroup,
} from '@chakra-ui/react';
import { XCircleIcon, DocumentIcon, FilmIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/tooltip';
import { FileType } from '@/lib/types';

interface MediaFile {
  file: string;
  name?: string;
  size?: number;
}

interface FileUploaderProps {
  files: MediaFile[];
  onUpload: (file: File) => void;
  onDelete: (file: string, index: number) => void;
  isUploading?: boolean;
  isDeletingIndex?: number | null;
  maxFileSizeMB?: number;
}

const getFileType = (fileUrl: string): FileType => {
  const extension = fileUrl.split('.').pop()?.toLowerCase() || '';

  // Common file extensions
  const imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'webp',
    'svg',
    'avif',
    'tiff',
    'heic',
  ];
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'm4v', '3gp'];

  if (imageExtensions.includes(extension)) return FileType.Image;
  if (videoExtensions.includes(extension)) return FileType.Video;

  // Try to guess from URL pattern
  if (fileUrl.match(/\.(jpg|jpeg|png|gif|webp)/i)) return FileType.Image;
  if (fileUrl.match(/\.(mp4|webm|mov)/i)) return FileType.Video;

  // Default to Image
  return FileType.Image;
};

const FileCard = memo(
  ({
    file,
    index,
    onDelete,
    isDeleting,
  }: {
    file: MediaFile;
    index: number;
    onDelete: (file: string, index: number) => void;
    isDeleting?: boolean;
  }) => {
    const [isError, setIsError] = useState(false);
    const fileType = getFileType(file.file);

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(file.file, index);
    };

    const handleError = () => {
      setIsError(true);
    };

    return (
      <Box
        key={`file-${index}`}
        borderWidth='1px'
        borderRadius='lg'
        borderColor='gray.200'
        overflow='hidden'
        position='relative'
        transition='all 0.2s'
        _hover={{ borderColor: 'gray.300', transform: 'translateY(-2px)' }}
        role='group'
      >
        <Center position='relative' bg='gray.50' h='160px' opacity={isDeleting ? 0.5 : 1}>
          {isDeleting && (
            <Box
              position='absolute'
              inset={0}
              bg='blackAlpha.50'
              zIndex={2}
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <Spinner size='md' color='gray.500' />
            </Box>
          )}

          {fileType === FileType.Image && !isError ? (
            <Image
              src={file.file}
              alt={file.name || 'Media file'}
              boxSize='160px'
              objectFit='cover'
              onError={handleError}
            />
          ) : fileType === FileType.Video ? (
            <Box as='video' boxSize='160px'>
              <source src={file.file} type='video/mp4' />
              <Center h='160px' w='100%'>
                <Icon as={FilmIcon} w={8} h={8} color='gray.400' />
                <Text fontSize='xs' mt={2}>
                  Video preview unavailable
                </Text>
              </Center>
            </Box>
          ) : (
            <Center h='160px' w='100%'>
              <Flex direction='column' align='center'>
                <Icon as={DocumentIcon} w={8} h={8} color='gray.400' />
                <Text fontSize='xs' mt={2} textAlign='center'>
                  {file.name || 'Media file'}
                </Text>
              </Flex>
            </Center>
          )}

          <Tooltip content='Remove media'>
            <IconButton
              variant='subtle'
              size={'xs'}
              color={'red'}
              aria-label={'Remove a media'}
              position={'absolute'}
              top={0.5}
              right={0.5}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Icon as={XCircleIcon} w={5} h={5} />
            </IconButton>
          </Tooltip>
        </Center>

        {file.name && (
          <Box p={2} bg='white'>
            <Text fontSize='xs' title={file.name}>
              {file.name}
            </Text>
            {file.size && (
              <Text fontSize='xs' color='gray.500'>
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </Text>
            )}
          </Box>
        )}
      </Box>
    );
  },
);

FileCard.displayName = 'FileCard';

const FileUploader: React.FC<FileUploaderProps> = ({
  files,
  onUpload,
  onDelete,
  isUploading = false,
  isDeletingIndex = null,
  maxFileSizeMB = 50,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const columns = useBreakpointValue({ base: 2, md: 3, lg: 4 }) || 4;

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = event.target.files;
      if (!newFiles || newFiles.length === 0) return;

      const selectedFile = newFiles[0];

      if (selectedFile.size > maxFileSizeMB * 1024 * 1024) {
        setFileError(`File exceeds maximum size of ${maxFileSizeMB}MB`);
        setTimeout(() => setFileError(null), 4000);
        return;
      }

      setFileError(null);
      onUpload(selectedFile);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onUpload, maxFileSizeMB],
  );

  const handleCardClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileChange({
          target: { files: e.dataTransfer.files },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [handleFileChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  return (
    <Box width={'full'}>
      <SimpleGrid gap={4} columns={columns} w='full'>
        {files.map((item, index) => (
          <FileCard
            key={`file-${index}`}
            file={item}
            index={index}
            onDelete={onDelete}
            isDeleting={isDeletingIndex === index}
          />
        ))}
      </SimpleGrid>

      <ButtonGroup w={'full'} mt={4} display={'flex'}>
        <Button
          variant='surface'
          size='sm'
          borderColor={dragActive ? 'blue.400' : 'gray.100'}
          borderStyle={dragActive || isUploading ? 'dashed' : 'solid'}
          onClick={isUploading ? undefined : handleCardClick}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onKeyDown={handleKeyDown}
          loading={isUploading}
          loadingText='Uploading...'
          flex={1}
        >
          <Icon as={CloudArrowUpIcon} w={4} h={4} /> Upload media file
          <Input
            type='file'
            ref={fileInputRef}
            accept='image/*,video/*'
            hidden
            onChange={handleFileChange}
            aria-hidden={true}
          />
        </Button>
      </ButtonGroup>

      {fileError && (
        <Text color='red.500' fontSize='sm' mt={2}>
          Error: {fileError}
        </Text>
      )}

      {maxFileSizeMB && (
        <Text color='gray.500' fontSize='xs' mt={2}>
          Max file size: {maxFileSizeMB}MB
        </Text>
      )}
    </Box>
  );
};

export default memo(FileUploader);
